/**
 * Player store for managing current player identity.
 * This store persists player identity across page refreshes.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { PlayerIdentity, Player } from '@/types/player'
import type { Locale } from '@/types/auth'

// Re-export Player type for convenience
export type { Player }

interface PlayerStoreState extends PlayerIdentity {
  isInitialized: boolean // FIX #1: Track if store has been synced after creation
  isWaiting: boolean // True when late-joined during PLAYING (poczekalnia)
  /** Reactive source of truth for in-game locale. Set on session entry/REST fetch, cleared on leave. */
  sessionLocale: Locale | null
  // Actions
  setPlayer: (
    id: string,
    name: string,
    sessionCode: string,
    sessionUuid: string | null,
    gameMode: string | null,
    isHost: boolean,
    role: 'phone' | 'tv',
    isWaiting?: boolean
  ) => void
  setIsWaiting: (isWaiting: boolean) => void
  setSessionLocale: (locale: Locale | null) => void
  clear: () => void
}

const initialState: PlayerIdentity & { isInitialized: boolean; isWaiting: boolean; sessionLocale: Locale | null } = {
  playerId: null,
  playerName: null,
  sessionCode: null,
  sessionUuid: null,
  gameMode: null,
  isHost: false,
  role: null,
  isInitialized: false,
  isWaiting: false,
  sessionLocale: null,
}

export const usePlayerStore = create<PlayerStoreState>()(
  persist(
    (set) => ({
      ...initialState,

      setPlayer: (id, name, sessionCode, sessionUuid, gameMode, isHost, role, isWaiting = false) => {
        set({
          playerId: id,
          playerName: name,
          sessionCode,
          sessionUuid,
          gameMode,
          isHost,
          role,
          isWaiting,
          isInitialized: true, // Mark store as initialized after setPlayer
        })
      },

      setIsWaiting: (isWaiting) => {
        set({ isWaiting })
      },

      setSessionLocale: (locale) => {
        set({ sessionLocale: locale })
      },

       clear: () => {
         set({ ...initialState, isInitialized: false })
       },
    }),
    {
      name: 'giermek-player',
      // Only persist identity fields
      partialize: (state) => ({
        playerId: state.playerId,
        playerName: state.playerName,
        sessionCode: state.sessionCode,
        sessionUuid: state.sessionUuid,
        gameMode: state.gameMode,
        isHost: state.isHost,
        role: state.role,
        isWaiting: state.isWaiting,
      }),
      // After rehydration from localStorage, mark store as initialized
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isInitialized = true
        }
      },
    }
  )
)
